import {addChild} from "../../../messageObject/messageObjectUtils.ts";
import {setupMetadata} from "../../../misc/messageMetadataUtils.ts";
import {getResources} from "../../../jsBridge/sender/shared/getResources.ts";
import {sendLlmRequest} from "./sendLlmRequest.ts";
import {QueuedMessage} from "../../../../GlobalStore/Slices/DynamicStateObjSlice";
import {setupUserMessage, toggleMessageImportance} from "../../../messageObject/messageObjectUtils.ts";
import {initializeNewChat} from "../../../db/chatHistory/chatMessagesUtils.ts";
import messageIdMapping from "../../../IdMappings/MessageIdMapping.ts";
import {v4 as uuidv4} from "uuid";

const setupLoadingMessage = (setMessages: any, currentMessageRef: any, messagesSequenceRef: any, messagesRef: any, model: string) => {
    let dots = '.';
    const aiMessage = {
        content: [{type: "text", "text": dots}],
        tool_calls: [],
        role: 'assistant',
        answeredBy: model,
        isLoading: true
    };
    addChild(aiMessage, currentMessageRef, messagesSequenceRef);
    return setInterval(() => {
        dots = dots.length < 3 ? dots + '.' : '.';
        currentMessageRef.current.message = {
            ...currentMessageRef.current.message,
            content: [{type: "text", text: dots}]
        };
        setMessages(messagesRef.current.deepCopy());
    }, 500);
}

export const processSubmit = (websocketRef: any, messagesRef: any, currentMessageRef: any, messagesSequenceRef: any, settingsObjRef: any, environmentObjRef: any, userObjRef: any, dynamicStateObjRef: any, llmRequestRefs: any, setupWebsocketHelper: any, setMessages: any, updateElementInProgress: any, setDynamicStateObj: any) => {
    llmRequestRefs.current.aiTurnCounter++;
    llmRequestRefs.current.toolCalls = {};

    setupMetadata(currentMessageRef);
    sendLlmRequest(websocketRef, settingsObjRef, environmentObjRef, userObjRef, dynamicStateObjRef, messagesRef, messagesSequenceRef, currentMessageRef, llmRequestRefs, setupWebsocketHelper, setMessages, updateElementInProgress, setDynamicStateObj);
};

export const handleSubmit = (setMessages: any, messagesRef: any, currentMessageRef: any, messagesSequenceRef: any, settingsObjRef: any, environmentObjRef: any, dynamicStateObjRef: any, setDynamicStateObj: any, updateElementInProgress: any, llmRequestRefs: any, processSubmitHelper: any) => {
    setMessages(messagesRef.current.deepCopy());
    if (currentMessageRef.current.message.role === "user") llmRequestRefs.current.userMessage = currentMessageRef.current;

    setDynamicStateObj({canSubmit: false, editingId: null, errorMessage: null});
    updateElementInProgress('', 'clear');
    updateElementInProgress('stream', 'add');

    if (llmRequestRefs.current.intervalId) clearInterval(llmRequestRefs.current.intervalId);
    llmRequestRefs.current.intervalId = setupLoadingMessage(setMessages, currentMessageRef, messagesSequenceRef, messagesRef, settingsObjRef.current.model);
    llmRequestRefs.current.aiMessage = currentMessageRef.current;
    if (llmRequestRefs.current.aiTurnCounter === 0) setDynamicStateObj({scrollToBottom: true});
    llmRequestRefs.current.messagesToWrite.push(currentMessageRef.current);

    const toolCallsInProgress = Object.keys(llmRequestRefs.current.toolCalls).length

    if (
        toolCallsInProgress === 0 && (
            environmentObjRef.current.environment.includes("jetbrains") ||
            environmentObjRef.current.environment === "vscode" ||
            (environmentObjRef.current.environment === "chrome" && settingsObjRef.current.contextMode !== "None")
        )
    ) {
        getResources(environmentObjRef.current.environment, dynamicStateObjRef, environmentObjRef.current.vscode, settingsObjRef);
    } else {
        processSubmitHelper();
    }
};

export const processQueuedMessage = (queuedMessage: QueuedMessage, messagesRef: any, currentMessageRef: any, messagesSequenceRef: any, settingsObjRef: any, userObjRef: any, dynamicStateObjRef: any, llmRequestRefs: any, setMessages: any, updateChatHistory: any, setDynamicStateObj: any, updateImportantMessageIds: any, handleSubmitHelper: any, updateMessageQueue: any) => {
    try {
        const { text, files, isImportant } = queuedMessage;

        // Initialize new chat if needed
        let conversationId = dynamicStateObjRef.current.conversationId;
        if (Object.keys(messageIdMapping).length === 0) {
            conversationId = uuidv4();
            initializeNewChat(text ?? "_", settingsObjRef.current.isIncognito, userObjRef.current.user, setDynamicStateObj, updateChatHistory, setMessages, messagesRef, currentMessageRef, conversationId);
        }

        const content = [];
        const filesList = Object.values(files);
        const filteredFiles = filesList.filter((file: any) => !(file.type === "link" && file.loadingInfo.task !== "done"));

        if (filteredFiles.length !== 0) {
            const sortedFiles = filteredFiles.sort((a: any, b: any) => {
                const typeOrder: Record<string, number> = { 'video': 1, 'image': 2, 'audio': 3, 'file': 3, 'link': 4 };
                return (typeOrder[a.type || 'file']) - (typeOrder[b.type || 'file']);
            });
            content.push(...sortedFiles);
        }

        if (text) {
            content.push({
                type: 'text',
                text: text
            });
        }

        const userMessage = setupUserMessage(content);
        llmRequestRefs.current.parentMessage = currentMessageRef.current;
        const messageId = uuidv4();
        addChild(userMessage, currentMessageRef, messagesSequenceRef, messageId);
        llmRequestRefs.current.messagesToWrite.push(currentMessageRef.current);

        if (isImportant) {
            toggleMessageImportance(userObjRef.current.user, settingsObjRef.current.isIncognito, conversationId, messageId, isImportant, updateImportantMessageIds, setMessages);
        }

        handleSubmitHelper();
        console.log(`Successfully processed queued message: ${queuedMessage.id}`);
    } catch (error) {
        console.error(`Failed to process queued message: ${queuedMessage.id}`, error);
        // Re-add the message to the queue to prevent data loss
        updateMessageQueue(queuedMessage, 'add');
        // Set error state to inform user
        setDynamicStateObj({
            errorMessage: `Failed to process queued message. Message has been re-added to queue.`
        });
    }
};
export const processMessageQueue = (messageQueue: QueuedMessage[], updateMessageQueue: any, messagesRef: any, currentMessageRef: any, messagesSequenceRef: any, settingsObjRef: any, userObjRef: any, dynamicStateObjRef: any, llmRequestRefs: any, setMessages: any, updateChatHistory: any, setDynamicStateObj: any, updateImportantMessageIds: any, handleSubmitHelper: any) => {
    if (messageQueue.length === 0 || !dynamicStateObjRef.current.canSubmit) {
        return;
    }

    const nextMessage = messageQueue[0];
    updateMessageQueue(nextMessage.id, 'remove');

    processQueuedMessage(nextMessage, messagesRef, currentMessageRef, messagesSequenceRef, settingsObjRef, userObjRef, dynamicStateObjRef, llmRequestRefs, setMessages, updateChatHistory, setDynamicStateObj, updateImportantMessageIds, handleSubmitHelper, updateMessageQueue);
};